import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaArrowLeft } from 'react-icons/fa';
import { signInWithEmailAndPassword } from "firebase/auth";
import { ref, get } from "firebase/database";
import { auth, db } from './firebase'; // Adjust path if needed
import { useAuth } from './AuthContext'; // Import the AuthContext

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth(); // Get login function from AuthContext
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear field-specific errors
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear submit error when user starts typing
    if (errors.submit) {
      setErrors(prev => ({ ...prev, submit: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email or Mobile Number is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Step 1: Check if user is Admin first
      const adminRef = ref(db, 'Dr/admin');
      const adminSnapshot = await get(adminRef);

      if (adminSnapshot.exists()) {
        const adminData = adminSnapshot.val();
        // Check if entered credentials match admin credentials
        // Admin can login with mobile number or email
        const isAdminMobile = formData.email === adminData.mobilenumber || formData.email === String(adminData.mobilenumber);
        const isAdminPassword = formData.password === adminData.password || formData.password === String(adminData.password);
        
        if (isAdminMobile && isAdminPassword) {
          console.log('Admin login successful:', adminData);
          
          // Store admin data in context and localStorage
          const adminUser = {
            uid: 'admin',
            email: adminData.email || 'admin@Anusaya.com',
            mobilenumber: adminData.mobilenumber,
            role: 'admin',
            name: 'Admin',
            isAdmin: true
          };
          
          // Use AuthContext login function
          login(adminUser, 'admin-token');
          
          // Explicitly set userRole in localStorage
          localStorage.setItem('userRole', 'admin');
          localStorage.setItem('userId', 'admin');
          localStorage.setItem('userName', 'Admin');
          
          navigate('/admin/dashboard');
          return;
        } else if (isAdminMobile && !isAdminPassword) {
          // Mobile number matches but password is wrong
          setErrors({ submit: 'Invalid password for admin account.' });
          return;
        }
      }

      // Step 2: Check if input is a mobile number (for regular user login)
      const mobilePattern = /^[0-9]{10}$/;
      if (mobilePattern.test(formData.email)) {
        // User is trying to login with mobile number
        const userRef = ref(db, `Dr/users/${formData.email}`);
        const userSnapshot = await get(userRef);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          // Check if password matches
          if (userData.password === formData.password) {
            console.log('User login successful:', userData);
            
            // Prepare user data for context
            const user = {
              uid: userData.uid || formData.email,
              email: userData.email || `${formData.email}@Anusaya.com`,
              mobilenumber: formData.email,
              role: 'user',
              name: userData.fullName || 'User',
              isAdmin: false,
              ...userData
            };
            
            // Generate a token for regular users (you can use Firebase Auth token or create your own)
            const token = `user-token-${Date.now()}`;
            
            // Use AuthContext login function
            login(user, token);
            
            // Store additional user info in localStorage
            localStorage.setItem('userRole', 'user');
            localStorage.setItem('userMobile', formData.email);
            localStorage.setItem('userId', user.uid);
            localStorage.setItem('userName', user.name);
            
            navigate('/');
            return;
          } else {
            setErrors({ submit: 'Invalid password. Please try again.' });
            return;
          }
        } else {
          setErrors({ submit: 'No account found with this mobile number. Please register first.' });
          return;
        }
      }

      // Step 3: If input is email format, try Firebase Auth for email-based users
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailPattern.test(formData.email)) {
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        const firebaseUser = userCredential.user;

        // Check if user profile exists in Realtime Database
        const userRef = ref(db, `Dr/users/${firebaseUser.uid}`);
        const userSnapshot = await get(userRef);

        let userData = {};
        if (userSnapshot.exists()) {
          userData = userSnapshot.val();
        }

        // Prepare user data for context
        const user = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          role: 'user',
          name: userData.fullName || firebaseUser.displayName || 'User',
          isAdmin: false,
          ...userData
        };

        // Get Firebase Auth token
        const token = await firebaseUser.getIdToken();
        
        // Use AuthContext login function
        login(user, token);
        
        // Store user info in localStorage
        localStorage.setItem('userRole', 'user');
        localStorage.setItem('userId', user.uid);
        localStorage.setItem('userName', user.name);
        
        console.log('Email user login successful:', user);
        navigate('/');
        return;
      }

      // If no valid login method found
      setErrors({ submit: 'Invalid credentials. Please check your mobile number/email and password.' });

    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Login failed. Please try again.';

      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          errorMessage = 'Invalid email or password.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        default:
          errorMessage = 'An unexpected error occurred. Please try again.';
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="p-8 md:p-12">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium mb-6">
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h2>
            <p className="text-gray-600">Sign in to your Anusaya account</p>
          </div>

          {/* Submit Error Message */}
          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email or Mobile Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter Your Mobile Number"
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`pl-10 pr-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isSubmitting}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 rounded-xl font-bold text-white flex items-center justify-center transition-all duration-300 ${
                isSubmitting
                  ? 'bg-teal-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-lg transform hover:scale-105'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-teal-600 hover:text-teal-500 underline">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;